import { useState } from "react";
import { Mail, Clock, MessageSquare } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setSent(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-14 grid grid-cols-1 md:grid-cols-2 gap-12">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">Contact</p>
        <h1 className="font-heading text-4xl uppercase font-black tracking-tighter mt-2">Need Help Choosing Gear?</h1>
        <p className="text-black/60 mt-4">Tell us what you're preparing for and we'll help you find the right products. Real people, practical answers.</p>
        <div className="mt-8 space-y-4 text-sm">
          <p className="flex items-center gap-3"><Mail size={16} className="text-[#4A5D23]" /> support@shodansecurity.com</p>
          <p className="flex items-center gap-3"><Clock size={16} className="text-[#4A5D23]" /> Mon–Fri, 9am–5pm ET</p>
          <p className="flex items-center gap-3"><MessageSquare size={16} className="text-[#4A5D23]" /> Typical response within 1 business day</p>
        </div>
        <div className="mt-10 border border-black/10 p-6 bg-[#F4F3F0]">
          <h3 className="font-heading font-bold uppercase tracking-tight text-sm mb-3">Common Questions</h3>
          <div className="space-y-3 text-xs text-black/60">
            <p><strong className="text-black">Shipping?</strong> Orders ship in 1–2 business days. Free over $75.</p>
            <p><strong className="text-black">Returns?</strong> 30-day hassle-free returns on unused items.</p>
            <p><strong className="text-black">Restricted items?</strong> Some products can't ship to all states — check local laws.</p>
            <p><strong className="text-black">Events?</strong> Find our booth schedule on the Events page.</p>
          </div>
        </div>
      </div>
      <div>
        {sent ? (
          <div data-testid="contact-success" className="border border-[#4A5D23]/30 bg-[#4A5D23]/5 p-10 text-center">
            <h3 className="font-heading font-bold uppercase tracking-tight">Message Sent</h3>
            <p className="text-sm text-black/60 mt-2">We'll get back to you within one business day.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="contact-name-input" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
            <input data-testid="contact-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
            <textarea data-testid="contact-message-input" required rows={6} placeholder="What are you preparing for?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full border border-black/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
            <button data-testid="contact-submit-btn" type="submit" className="w-full bg-[#121212] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Send Message</button>
          </form>
        )}
      </div>
    </div>
  );
}

export function About() {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">About</p>
      <h1 className="font-heading text-4xl uppercase font-black tracking-tighter mt-2">Shodan Security</h1>
      <div className="mt-8 space-y-5 text-black/70 leading-relaxed">
        <p><strong className="text-black">Protect. Prepare. Perform.</strong> Shodan Security provides professional-grade equipment for personal security, emergency preparedness, tactical organization, outdoor adventure, and everyday readiness.</p>
        <p>We believe preparedness isn't paranoia — it's planning. Our catalog is built for responsible, security-conscious people: prepared citizens, outdoor enthusiasts, everyday carriers, and families who want practical options when the unexpected happens.</p>
        <p>You'll find us online and in person at gun shows, preparedness expos, outdoor events, and local markets. Same gear, same standards, either way.</p>
      </div>
      {[["shipping", "Shipping Policy", "Orders ship within 1–2 business days from the USA. Free standard shipping on qualifying orders over $75; flat $7.95 otherwise. Some items have state/local shipping restrictions — we do not ship restricted products to jurisdictions where they are prohibited."],
        ["returns", "Return Policy", "30-day hassle-free returns on unused items in original packaging. Contact support to start a return. Refunds are issued to the original payment method."],
        ["privacy", "Privacy Policy", "We collect only the information needed to fulfill orders and provide service. We never sell your personal data. Email subscribers can unsubscribe at any time."],
        ["terms", "Terms of Service", "Products are intended for lawful use only. Buyers are responsible for compliance with state and local laws. Content on this site is educational and is not legal advice. All sales subject to our shipping and return policies."]].map(([id, title, text]) => (
        <section key={id} id={id} className="mt-10 border-t border-black/10 pt-8">
          <h2 className="font-heading text-xl uppercase font-bold tracking-tight">{title}</h2>
          <p className="text-sm text-black/60 mt-3 leading-relaxed">{text}</p>
        </section>
      ))}
    </div>
  );
}
